/**
 * Locale translations: it (Italian).
 * Keys and structure are shared across all locale files.
 */
export const it = {
  // Common
  common: {
    loading: "Caricamento...",
    error: "Errore",
    sort: "Ordina",
    sortAsc: "Ordine crescente",
    sortDesc: "Ordine decrescente",
    save: "Salva",
    cancel: "Annulla",
    delete: "Elimina",
    remove: "Rimuovi",
    edit: "Modifica",
    view: 'Visualizza',
    copy: "Copia",
    create: "Crea",
    update: "Aggiorna",
    search: "Cerca",
    actions: "Azioni",
    code: "Codice",
    all: "Tutti",
    clear: "Pulisci",
    filter: "Filtro",
    yes: "Sì",
    no: "No",
    confirm: "Conferma",
    close: "Chiudi",
    select: "Seleziona",
    active: "Attivo",
    inactive: "Inattivo",
    success: "Successo",
    updated: "Aggiornato",
    created: "Creato",
    deleted: "Eliminato",
    confirmDelete: "Sei sicuro di voler eliminare?",
    errorOccurred: "Si è verificato un errore",
    noData: "Nessun dato",
    status: "Stato",
    show: "Mostra",
    hide: "Nascondi",
    searchPlaceholder: "Cerca...",
    allStatuses: "Tutti gli stati",
    clearFilters: "Pulisci filtri",
    newRecord: "Nuovo record",
    noPermission: "Nessun permesso",
    statusDistribution: "Distribuzione stato",
    rows: "righe",
    firstPage: "Prima pagina",
    previousPage: "Pagina precedente",
    nextPage: "Pagina successiva",
    lastPage: "Ultima pagina",
    page: "Pagina",
    optional: "Opzionale",
    saving: "Salvataggio...",
    deleteConfirmTitle: "Conferma eliminazione",
    deleteWarning: "Questa azione non può essere annullata!",
    autoTranslate: "Traduzione automatica",
    autoTranslateSuccessCount: "{count} elementi tradotti automaticamente.",
    autoTranslateNoSource: "Traduzioni sorgente non trovate.",
    enterTargetLanguage: "Scegli la lingua di destinazione.",
    sourceLanguageLabel: "Lingua sorgente (la tua interfaccia)",
    targetLanguage: "Lingua di destinazione",
    sourceAndTargetMustDiffer: "La lingua sorgente e quella di destinazione devono differire.",
    context: "Contesto",
    key: "Chiave",
    value: "Valore",
    locale: "Lingua",
    auto: "Auto",
    manual: "Manuale",
    manageLocales: "Gestisci lingue",
    activeLocalesDescription: "Scegli le lingue attive. Salvato solo come impostazione.",
    updateUntranslatedFields: "Sincronizza nuovi campi",
    syncEntriesResult: "Sincronizzate {count} nuove voci di traduzione.",
    syncDeletedResult: "Eliminate {count} voci orfane.",
    selectSourceLanguageForSync: "In che lingua sono i contenuti?",
    sourceLanguage: "Lingua sorgente",
    originalShort: "Orig.",
    languageNames: { en: "Inglese", tr: "Turco", de: "Tedesco", fr: "Francese", es: "Spagnolo", ar: "Arabo", it: "Italiano", ja: "Giapponese", zh: "Cinese", ru: "Russo" } as Record<string, string>,
    perPage: "Per pagina",
    darkMode: "Tema scuro",
    lightMode: "Tema chiaro",
    copyright: "© {year} QubePro. Tutti i diritti riservati.",
    translationRates: "Tassi di traduzione",
    profileMenu: "Menu profilo",
    manageYourAccount: "Gestisci account",
    adminConsole: "Console di amministrazione",
    serverConfiguring: "Configurazione server in corso, attendere...",
    productName: "Gestione",
  },

  portal: {
    searchPlaceholder: "Cerca applicazioni...",
    noAppsFound: "Nessuna applicazione trovata.",
    quickLinks: "Link rapidi",
    console: "Console amministratore",
    lastLogin: "Ultimo accesso",
    apps: {
      core: { name: "Gestione", description: "Gestisci aziende, organizzazioni, utenti, ruoli e permessi in una piattaforma centralizzata." },
      bi: { name: "Analisi", description: "Crea report e dashboard dai tuoi dati. Trasforma i dati aziendali in intuizioni." },
      bpm: { name: "Processi", description: "Avvia e completa richieste interne, approvazioni e attività digitalmente." },
      support: { name: "Supporto", description: "Gestisci le richieste dei clienti e i ticket. Risolvi i problemi in un unico posto." },
      agent: { name: "Automazione", description: "Automatizza i compiti di routine con bot e agenti. Si integra con i tuoi strumenti." },
      ai: { name: "IA e Previsioni", description: "Usa l'IA per analisi dati e previsioni. Migliora la pianificazione." },
      erp: { name: "Suite Business", description: "Moduli per finanza, vendite, magazzino e operazioni. Gestisci tutto su una piattaforma." },
      pms: { name: "Progetti", description: "Pianifica i progetti, assegna i compiti, segui i progressi e collabora con il team." },
      crm: { name: "Clienti", description: "Gestisci contatti, lead, offerte e pipeline di vendita in un unico posto." },
    },
    menus: {
      core_dashboard: "Dashboard",
      core_companies: "Aziende",
      core_organizations: "Organizzazioni",
      core_roles: "Ruoli",
      core_users: "Utenti",
      core_groups: "Gruppi",
      core_emails: "Account email",
      core_data_sources: "Sorgenti dati",
      core_settings_company: "Impostazioni azienda",
      core_settings_user: "Impostazioni utente",
      core_translations: "Traduzioni",
    },
  },

  panel: {
    apps: {
      crm: { name: "CRM", footerName: "CRM" },
      accounting: { name: "Contabilità" },
      finance: { name: "Finanza" },
      procurement: { name: "Approvvigionamento" },
      inventory: { name: "Magazzino" },
      purchase: { name: "Acquisti" },
      sales: { name: "Vendite" },
      manufacturing: { name: "Produzione" },
      assets: { name: "Cespiti" },
      hr: { name: "Risorse umane" },
      project: { name: "Progetti" },
      timesheet: { name: "Time sheet" },
      documents: { name: "Documenti" },
      pos: { name: "Punto vendita" },
      rental: { name: "Noleggio" },
      dashboard: { name: "Report" },
    },
    appsLabel: "Applicazioni",
    settingsLabel: "Impostazioni",
    coreLabel: "Gestione",
    backToHome: "Torna alla home",
  },

  errors: {
    unknown: "Si è verificato un errore sconosciuto",
    network: "Impossibile connettersi al server. Controlla la tua connessione Internet.",
    timeout: "Tempo di attesa scaduto. Riprova.",
    badRequest: "Richiesta non valida",
    unauthorized: "Sessione scaduta. Effettua nuovamente il login.",
    forbidden: "Non hai i permessi per questa azione.",
    notFound: "Risorsa non trovata",
    tooManyRequests: "Troppe richieste. Riprova più tardi.",
    internalServerError: "Errore del server. Riprova più tardi.",
    badGateway: "Server momentaneamente non disponibile",
    serviceUnavailable: "Servizio momentaneamente non disponibile",
    gatewayTimeout: "Il server non risponde",
    serverErrorWithStatus: "Errore del server ({status})",
  },

  // Dashboard
  dashboard: {
    title: "Dashboard",
    subtitle: "Riepilogo del sistema di gestione centralizzato",
    welcome: "Benvenuto",
    guestName: "Ospite",
    statusSummary: "Riepilogo stato attuale",
    currentTimeLabel: "Ora attuale",
    todayLabel: "Oggi",
    activeCompanies: "Aziende attive",
    activeOrganizations: "Organizzazioni attive",
    activeUsers: "Utenti attivi",
    activeRoles: "Ruoli attivi",
    database: "Database",
    databaseUsed: "Spazio utilizzato",
    recentlyAddedUsers: "Utenti aggiunti di recente",
    recentlyAddedCompanies: "Aziende aggiunte di recente",
    noUsersYet: "Nessun utente presente",
    noCompaniesYet: "Nessuna azienda presente",
    userPanel: "Pannello utente",
    userPanelWelcome: "Benvenuto! Accedi alle impostazioni dal menu.",
    quickActions: "Azioni rapide",
    systemHealthTitle: "Salute del sistema",
    systemLogsTitle: "Log di sistema",
    logChartTitle: "Distribuzione Errori/Avvisi",
    logErrorsLabel: "Errori",
    logWarningsLabel: "Avvisi",
    logSuccessLabel: "Successo",
    statusHealthy: "In salute",
    statusFailed: "Fallito",
    statusUnknown: "Sconosciuto",
    lastRun: "Ultima esecuzione",
    failures24h: "Guasti 24h",
    noData: "Nessun dato",
    systemComponents: {
      EMAIL_INGEST: "Posta in arrivo (IMAP)",
      EMAIL_DISPATCH: "Posta in uscita (SMTP)",
      JOB_SCHEDULER: "Pianificatore",
      API: "API",
    },
  },

  // Sidebar
  sidebar: {
    dashboard: "Dashboard",
    roles: "Ruoli",
    users: "Utenti",
    settings: "Impostazioni",
    systemSettings: "Impostazioni sistema",
    userSettings: "Impostazioni utente",
    organizations: "Organizzazioni",
    logout: "Logout",
    toggleMenu: "Apri menu",
    toggleMenuClose: "Chiudi menu",
    management: "Gestione",
    companies: "Aziende",
    emailAccounts: "Account email",
    dataSources: "Sorgenti dati",
    translations: "Traduzioni",
  },

  // Settings
  settings: {
    title: "Impostazioni",
    subtitle: "Gestisci le impostazioni aziendali e utente",
    companySettingsSubtitle: "Impostazioni generali dell'azienda",
    userSettingsSubtitle: "Le tue impostazioni personali",
    securitySettingsSubtitle: "Gestione password e sicurezza",
    companySettings: "Impostazioni Azienda",
    userSettings: "Impostazioni Utente",
    general: "Generale",
    personal: "Personale",
    timezone: "Fuso orario",
    dateFormat: "Formato data",
    defaultCurrency: "Valuta predefinita",
    defaultLanguage: "Lingua predefinita",
    language: "Lingua",
    companySettingsSaved: "Impostazioni aziendali salvate",
    userSettingsSaved: "Impostazioni utente salvate",
    failedToLoad: "Errore nel caricamento delle impostazioni",
    saveFailed: "Salvataggio delle impostazioni fallito",
    profilePicture: "Foto profilo",
    maxFileSize: "Max 5MB. Formati: JPEG, PNG, GIF, WebP",
    change: "Cambia",
    uploading: "Caricamento...",
    deleteAvatar: "Elimina foto",
    deleteAvatarConfirm: "Vuoi eliminare la foto profilo?",
    avatarUploaded: "Foto profilo caricata",
    avatarDeleted: "Foto profilo eliminata",
    avatarUploadError: "Errore nel caricamento della foto",
    avatarDeleteError: "Errore nell'eliminazione della foto",
    onlyImageFiles: "Sono accettate solo immagini",
    fileSizeError: "Il file deve essere inferiore a 5MB",
    appearance: "Aspetto",
    theme: "Tema",
    light: "Chiaro",
    dark: "Scuro",
    pageSize: "Dimensione pagina tabella",
    defaultCompany: "Azienda predefinita",
    selectDefaultCompany: "Seleziona l'azienda predefinita",
    noCompaniesAssigned: "Nessuna azienda assegnata",
    security: "Sicurezza",
    changePassword: "Cambia password",
    currentPassword: "Password attuale",
    newPassword: "Nuova password",
    confirmNewPassword: "Conferma nuova password",
    passwordChanged: "Password cambiata con successo",
    passwordChangeFailed: "Cambio password fallito",
    passwordMismatch: "Le password non corrispondono",
    passwordTooShort: "La password deve contenere almeno 6 caratteri",
    passwordSameAsOld: "La nuova password deve essere diversa dalla attuale",
    changingPassword: "Cambio password in corso...",
    pleaseSelectCompany: "Seleziona un'azienda",
  },

  // Organizations
  organizations: {
    title: "Organizzazioni",
    code: "Codice",
    name: "Nome organizzazione",
    description: "Descrizione",
    logo: "Logo",
    logoEmpty: "Nessun logo",
    uploadLogo: "Carica logo",
    status: "Stato",
    smtp: "SMTP",
    imap: "IMAP",
    smtpEmailAccount: "Account email SMTP",
    imapEmailAccount: "Account email IMAP",
    active: "Attiva",
    inactive: "Inattiva",
    newOrganization: "Nuova organizzazione",
    editOrganization: "Modifica organizzazione",
    deleteConfirm: "Vuoi eliminare questa organizzazione?",
    deleteFailed: "Eliminazione organizzazione fallita",
    operationFailed: "Operazione fallita",
    failedToLoad: "Caricamento organizzazioni fallito",
    dataFormatError: "Formato dati organizzazione inatteso",
    searchPlaceholder: "Cerca organizzazione (nome, descrizione)",
    empty: "Nessun dato",
    notSet: "Non impostato",
    noCreatePermission: "Nessun permesso per creare organizzazioni",
    noUpdatePermission: "Nessun permesso per modificare organizzazioni",
    noDeletePermission: "Nessun permesso per eliminare organizzazioni",
    company: "Nome azienda",
    statusDistribution: "Distribuzione stato organizzazioni",
  },

  // Users
  users: {
    title: "Utenti",
    newUser: "Nuovo utente",
    editUser: "Modifica utente",
    noUsersYet: "Nessun utente presente",
    failedToLoad: "Caricamento utenti fallito",
    deleteConfirm: "Vuoi eliminare questo utente?",
    deleteFailed: "Eliminazione utente fallita",
    operationFailed: "Operazione fallita",
    passwordRequired: "Password obbligatoria",
    passwordChangeHint: "(Compila per modificare)",
    fullName: "Nome completo",
    email: "Email",
    status: "Stato",
    roles: "Ruoli",
    actions: "Azioni",
    firstName: "Nome *",
    lastName: "Cognome *",
    password: "Password",
    noRoles: "Nessun ruolo",
    rolesNotFound: "Nessun ruolo trovato",
    statusActive: "Attivo",
    statusInactive: "Inattivo",
    statusSuspended: "Sospeso",
    userInfoFailed: "Errore caricamento info utente",
    rolesFailed: "Errore caricamento ruoli",
    copy: "Copia",
    isAdmin: "Admin",
    groups: "Gruppi",
    groupCount: "Numero gruppi",
    userGroupName: "Nome gruppo",
    userGroupCode: "Codice gruppo",
    userGroupEmail: "Email gruppo",
    groupManager: "Responsabile gruppo",
    members: "Membri",
    userGroupDescription: "Descrizione",
    userGroupCreate: "Crea nuovo gruppo",
    userGroupEdit: "Modifica gruppo",
    manageMembers: "Gestisci membri",
    addMember: "Aggiungi membro",
    memberCount: "Numero membri",
    searchUsers: "Cerca utenti...",
    noMembers: "Nessun membro presente",
    selectUser: "Seleziona utente",
    addUser: "Aggiungi",
    removeUser: "Rimuovi",
    manageGroups: "Gestisci gruppi",
    noUsersFound: "Nessun utente trovato",
    statusDistribution: "Distribuzione stato utenti",
    groupStatusDistribution: "Distribuzione stato gruppi",
  },

  // Roles
  roles: {
    title: "Ruoli",
    newRole: "Nuovo ruolo",
    editRole: "Modifica ruolo",
    noRolesYet: "Nessun ruolo presente",
    failedToLoad: "Caricamento ruoli fallito",
    deleteConfirm: "Vuoi eliminare questo ruolo?",
    deleteFailed: "Eliminazione ruolo fallita",
    operationFailed: "Operazione fallita",
    dataFormatError: "Errore formato dati",
    permissionsFailed: "Errore caricamento permessi",
    name: "Nome ruolo *",
    description: "Descrizione",
    permissions: "Permessi",
    permissionCount: "Numero permessi",
    type: "Tipo ruolo",
    typeSystem: "Sistema",
    typeCustom: "Personalizzato",
    typeAll: "Tutti i ruoli",
    searchPlaceholder: "Cerca (nome, descrizione, permesso)...",
    systemBadge: "Sistema",
    permissionsCount: "Permessi ({count})",
    morePermissions: "+{count} altri",
    copy: "Copia",
    copySuffix: " (copia)",
    clearFilters: "Pulisci filtri",
    noCreatePermission: "Nessun permesso per creare ruoli",
    noUpdatePermission: "Nessun permesso per modificare ruoli",
    noDeletePermission: "Nessun permesso per eliminare ruoli",
    systemRoleEditBlocked: "I ruoli di sistema non possono essere modificati",
    systemRoleDeleteBlocked: "I ruoli di sistema non possono essere eliminati",
    noPermissions: "Nessun permesso",
    paginationRange: "{start}-{end} di {total}",
    paginationPage: "Pagina {page} / {total}",
    modules: {
      users: "Utenti",
      groups: "Gruppi",
      userGroupName: "Nome gruppo",
      userGroupCode: "Codice gruppo",
      groupManager: "Responsabile gruppo",
      members: "Membri",
      userGroupDescription: "Descrizione",
      userGroupCreate: "Crea nuovo gruppo",
      userGroupEdit: "Modifica gruppo",
      roles: "Ruoli",
      tenant: "Tenant",
      userSettingField: "Impostazione",
      settings: "Impostazioni",
      organizations: "Organizzazioni",
    },
    groups: "Gruppi",
    userGroup: "Gruppo",
    userGroupList: "Lista gruppi",
    userGroupCreate: "Crea gruppo",
    userGroupEdit: "Modifica gruppo",
    userGroupName: "Nome gruppo",
    userGroupDescription: "Descrizione",
    userGroupCode: "Codice",
    groupManager: "Responsabile gruppo",
    members: "Membri",
    addMember: "Aggiungi membro",
    assignRole: "Assegna ruolo",
    assign: "Assegna",
    statusDistribution: "Distribuzione stato ruoli",
    assignRoleMatrix: "Matrice ruoli",
    roleMatrix: "Matrice ruoli",
    selectCompanyToViewMatrix: "Seleziona azienda per la matrice",
    show: "Mostra",
    hide: "Nascondi",
    searchPermissions: "Cerca permessi...",
    permission: "Permesso",
    permissionsSelected: "{count} permessi selezionati",
  },

  // Companies
  companies: {
    title: "Aziende",
    newCompany: "Nuova azienda",
    editCompany: "Modifica azienda",
    noCompaniesYet: "Nessuna azienda presente",
    failedToLoad: "Caricamento aziende fallito",
    deleteConfirm: "Vuoi eliminare questa azienda?",
    deleteFailed: "Eliminazione fallita",
    operationFailed: "Operazione fallita",
    dataFormatError: "Errore formato dati",
    name: "Nome azienda *",
    status: "Stato",
    statusActive: "Attiva",
    statusInactive: "Inattiva",
    searchPlaceholder: "Cerca (nome, stato)...",
    clearFilters: "Pulisci filtri",
    copy: "Copia",
    copySuffix: " (copia)",
    noCreatePermission: "Nessun permesso per creare aziende",
    noUpdatePermission: "Nessun permesso per modificare aziende",
    noDeletePermission: "Nessun permesso per eliminare aziende",
    paginationRange: "{start}-{end} di {total}",
    paginationPage: "Pagina {page} / {total}",
    allStatuses: "Tutti gli stati",
    statusDistribution: "Distribuzione stato aziende",
    show: "Mostra",
    hide: "Nascondi",
    logo: "Logo",
    logoEmpty: "Nessun logo",
    uploadLogo: "Carica logo",
    smtpEmailAccount: "Account email SMTP",
    imapEmailAccount: "Account email IMAP",
    smtpEmailId: "Account email SMTP",
    imapEmailId: "Account email IMAP",
  },

  // Emails
  emails: {
    title: "Account Email",
    newEmail: "Nuovo account email",
    editEmail: "Modifica account email",
    noEmailsYet: "Nessun account presente",
    failedToLoad: "Caricamento account email fallito",
    deleteConfirm: "Vuoi eliminare questo account?",
    deleteConfirmMessage: "Vuoi eliminare l'account \"{name}\"?",
    deleteFailed: "Eliminazione fallita",
    operationFailed: "Operazione fallita",
    dataFormatError: "Errore formato dati",
    name: "Nome account",
    type: "Tipo",
    host: "Host",
    port: "Porta",
    user: "Nome utente",
    password: "Password",
    fromEmail: "Email mittente",
    secure: "Sicuro (SSL/TLS)",
    secureConnection: "Connessione sicura (SSL/TLS)",
    status: "Stato",
    statusActive: "Attivo",
    statusInactive: "Inattivo",
    activate: "Attiva",
    deactivate: "Disattiva",
    isActive: "Attivo",
    searchPlaceholder: "Cerca (nome, host, utente)...",
    clearFilters: "Pulisci filtri",
    copy: "Copia",
    noCreatePermission: "Nessun permesso per creare account",
    noUpdatePermission: "Nessun permesso per modificare account",
    noDeletePermission: "Nessun permesso per eliminare account",
    paginationRange: "{start}-{end} di {total}",
    paginationPage: "Pagina {page} / {total}",
    allStatuses: "Tutti gli stati",
    allTypes: "Tutti i tipi",
    statusDistribution: "Distribuzione stato account",
    show: "Mostra",
    hide: "Nascondi",
    nameRequired: "Il nome account è obbligatorio",
    hostRequired: "L'host è obbligatorio",
    portRequired: "La porta è obbligatoria",
    userRequired: "Il nome utente è obbligatorio",
    passwordRequired: "La password è obbligatoria",
    leaveBlankToKeep: "Lascia vuoto per non modificare",
    noData: "Nessun dato",
    noEmailsMessage: "Nessun account trovato",
    passwordEditHint: "Lascia vuoto per non modificare",
    noSearchResults: "Nessun risultato",
    namePlaceholder: "es. Supporto o info@azienda.com",
    deleteConfirmTitle: "Elimina account",
  },

  // Login
  login: {
    title: "QubePro ERP",
    subtitle: "Pianificazione delle risorse aziendali",
    heroTitle: "Gestione aziendale da un unico punto",
    heroSubtitle: "QubePro ERP unifica la gestione di aziende, organizzazioni, utenti e ruoli in un'unica piattaforma.",
    languageToggleLabel: "Lingua",
    turkish: "Turco",
    english: "Inglese",
    email: "Email",
    password: "Password",
    emailPlaceholder: "esempio@dominio.com",
    emailHint: "Il tenant viene determinato dal dominio automaticamente",
    passwordPlaceholder: "••••••••",
    loginButton: "Accedi",
    loggingIn: "Accesso...",
    loginFailed: "Accesso fallito",
    darkMode: "Tema scuro",
    lightMode: "Tema chiaro",
    loginPageTitle: "Accesso",
    submitButton: "Accedi",
    featureTitle: "Sistema di gestione centralizzato",
    featureDescription: "Gestisci le relazioni con i clienti, le vendite, l'inventario e i processi finanziari in un'unica piattaforma.",
    featureCard1Title: "Gestione clienti",
    featureCard1Description: "Traccia clienti, vendite potenziali e relazioni commerciali da un'unica posizione centrale.",
    featureCard2Title: "Vendite e ordini",
    featureCard2Description: "Gestisci facilmente preventivi, ordini e processi di vendita per aumentare i ricavi.",
    featureCard3Title: "Report e analisi",
    featureCard3Description: "Monitora le prestazioni aziendali con report in tempo reale e prendi decisioni basate sui dati.",
    featureCard4Title: "Integrazione e impostazioni",
    featureCard4Description: "Personalizza il sistema, integra altri strumenti e automatizza i processi aziendali.",
    pleaseWait: "Attendere prego",
    serverConfiguring: "Configurazione server in corso, attendere...",
    dbCreating: "Creazione database e configurazione. Potrebbe richiedere alcuni secondi.",
    emailLabel: "Email",
    passwordLabel: "Password",
    emailPlaceholderFull: "Inserisci la tua email",
    passwordPlaceholderFull: "Inserisci la password",
    showPassword: "Mostra password",
    hidePassword: "Nascondi password",
    emailRequired: "L'email è obbligatoria",
    passwordRequired: "La password è obbligatoria",
    backendUnavailable: "Server non disponibile. Riprova più tardi.",
    backendConnectFailed: "Connessione al server fallita. Riprova più tardi.",
    backendConnectFailedDev: "Connessione fallita. Assicurati che il backend sia attivo ({apiUrl}).",
    fieldEmail: "Email",
    fieldPassword: "Password",
    errors: {
      invalidCredentials: "Email o password errati. Riprova.",
      databaseUnavailable: "Database non disponibile o configurazione richiesta. Riprova.",
      accountInactive: "Account non attivo. Contatta l'amministratore.",
      sessionExpired: "Sessione scaduta. Effettua nuovamente il login.",
      tooManyAttempts: "Troppi tentativi. Riprova più tardi.",
      sessionTooLarge: "Dati sessione troppo grandi. Riprova.",
    },
  },

  // DataSources
  dataSources: {
    title: "Sorgenti Dati",
    subtitle: "Gestione connessioni ai database dei clienti",
    newDataSource: "Nuova sorgente dati",
    editDataSource: "Modifica sorgente dati",
    noDataSourcesYet: "Nessuna sorgente presente",
    failedToLoad: "Caricamento sorgenti fallito",
    deleteConfirm: "Vuoi eliminare questa sorgente dati?",
    deleteFailed: "Eliminazione fallita",
    operationFailed: "Operazione fallita",
    dataFormatError: "Errore formato dati",
    name: "Nome mostrato",
    driver: "Driver",
    server: "Server",
    port: "Porta",
    databaseName: "Nome database",
    username: "Nome utente",
    password: "Password",
    schema: "Schema",
    sourceType: "Tipo sorgente",
    source: "Sorgente",
    target: "Destinazione",
    sourceSystem: "Sistema sorgente",
    targetSystem: "Sistema destinazione",
    status: "Stato",
    statusActive: "Attiva",
    statusInactive: "Inattiva",
    searchPlaceholder: "Cerca (nome, server, database)...",
    clearFilters: "Pulisci filtri",
    copy: "Copia",
    paginationRange: "{start}-{end} / {total}",
    paginationPage: "Pagina {page} / {total}",
    allStatuses: "Tutti gli stati",
    allTypes: "Tutti",
    statusDistribution: "Distribuzione stato sorgenti",
    show: "Mostra",
    hide: "Nascondi",
    nameRequired: "Il nome è obbligatorio",
    serverRequired: "L'indirizzo server è obbligatorio",
    databaseRequired: "Il nome database è obbligatorio",
    passwordRequired: "La password è obbligatoria",
    testConnection: "Test connessione",
    testing: "Test in corso...",
    testSuccess: "Connessione riuscita!",
    testFailed: "Connessione fallita: ",
    integratedSecurity: "Sicurezza integrata",
    ssl: "SSL",
    connectionTimeout: "Timeout connessione (sec)",
    description: "Descrizione",
    noCreatePermission: "Nessun permesso per creare sorgenti",
    noUpdatePermission: "Nessun permesso per modificare sorgenti",
    noDeletePermission: "Nessun permesso per eliminare sorgenti",
  },

  aiProviders: {
    title: "Provider di IA",
    subtitle: "Gestisci modelli e integrazioni di IA",
    newProvider: "Nuovo provider di IA",
    editProvider: "Modifica provider di IA",
    noProvidersYet: "Nessun dato",
    failedToLoad: "Caricamento provider IA fallito",
    deleteConfirm: "Sei sicuro di voler eliminare questo provider di IA?",
    deleteFailed: "Eliminazione provider IA fallita",
    operationFailed: "Operazione fallita",
    name: "Nome del provider *",
    provider: "Tipo di provider",
    baseUrl: "URL di base",
    apiKey: "Chiave API",
    description: "Descrizione",
    status: "Stato",
    statusActive: "Attivo",
    statusInactive: "Inattivo",
    searchPlaceholder: "Cerca (nome, tipo, URL)...",
    clearFilters: "Cancella filtri",
    noCreatePermission: "Non hai il permesso di creare provider di IA",
    statusDistribution: "Distribuzione stato provider IA",
    baseUrlPlaceholder: "es. http://localhost:11434, https://api.custom.ai/v1",
    baseUrlEmptyDefault: "Lascia vuoto per i valori predefiniti",
    apiKeyPlaceholder: "Inserisci chiave API",
  },
};
